import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ComposedChart, Line,
} from 'recharts';
import {
  Microscope, RefreshCw, AlertTriangle, X, Database, ShieldAlert, Users2,
  Layers, Clock, Building2, ExternalLink, Info, CalendarRange, Save,
} from 'lucide-react';
import { authAPI, postAnalysisAPI, testPeriodsAPI } from '../services/api';
import type {
  PsaOverview, PsaCategory, PsaTemporal, PsaDailyPoint, PsaDemographics,
  PsaAdvertiser, PsaSourceResult, TestPeriod,
  PsaNo4PerMember, PsaNo4TypeMix, PsaNo4Parity, PsaReport0807, PsaReport0807Parity,
} from '../types';
import KpiCard from '../components/KpiCard';
import { GAMBLING_COLORS } from '../lib/chartColors';

const GENRES = ['gambling_public', 'gambling_pachinko', 'gambling_gacha_premium', 'gambling_online', 'gambling_game'] as const;
const CAT_LABEL: Record<string, string> = {
  gambling_public: '公営競技', gambling_pachinko: 'パチンコ', gambling_gacha_premium: 'オンラインくじ(グレー)',
  gambling_online: 'オンラインカジノ(違法)', gambling_game: 'ゲーム',
  other: 'その他', culture: '文化・エンタメ', economics: '経済・小売・金融', tourism: '観光・旅行',
  healthcare: '医療・健康', education: '教育', sports: 'スポーツ', politics: '政治・公共',
  technology: '技術', beauty: '美容', food: '食品', youtube: 'YouTube',
};
const LEGAL_LABEL: Record<string, string> = { legal: '合法', gray: 'グレーゾーン', illegal: '違法' };
const LEGAL_COLOR: Record<string, string> = { legal: '#22c55e', gray: '#f59e0b', illegal: '#ef4444' };
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DOW_JA = ['日', '月', '火', '水', '木', '金', '土'];
const fmt = (n: number | undefined) => (typeof n === 'number' ? n.toLocaleString() : '—');
const PLAT_LABEL: Record<string, string> = {
  youtube: 'YouTube', instagram: 'Instagram', facebook: 'Facebook', x_twitter: 'X (Twitter)',
};
const platLabel = (p: string) => PLAT_LABEL[p] || p;
const catLabel = (c: string) => CAT_LABEL[c] || c;
const catColor = (c: string) => GAMBLING_COLORS[c] || '#94a3b8';

export default function PostSurveyAnalysis() {
  const { t } = useTranslation();
  const [role, setRole] = useState<string | null>(null);
  const isSasadmin = role === 'sasadmin';

  const [overview, setOverview] = useState<PsaOverview | null>(null);
  const [categories, setCategories] = useState<PsaCategory[]>([]);
  const [temporal, setTemporal] = useState<PsaTemporal | null>(null);
  const [daily, setDaily] = useState<PsaDailyPoint[]>([]);
  const [demo, setDemo] = useState<PsaDemographics | null>(null);
  const [genre, setGenre] = useState<string>('all');
  const [advertisers, setAdvertisers] = useState<PsaAdvertiser[]>([]);
  // No.4 — per-user-ID counts, ad-type composition, and the report-parity check
  const [no4, setNo4] = useState<PsaNo4PerMember | null>(null);
  const [mix, setMix] = useState<PsaNo4TypeMix | null>(null);
  const [parity, setParity] = useState<PsaNo4Parity | null>(null);
  const [r0807, setR0807] = useState<PsaReport0807 | null>(null);
  const [r0807Parity, setR0807Parity] = useState<PsaReport0807Parity | null>(null);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // test periods + selected range (inclusive JST)
  const [periods, setPeriods] = useState<TestPeriod[]>([]);
  const [selFrom, setSelFrom] = useState('2026-07-06');
  const [selTo, setSelTo] = useState('2026-07-19');
  const [saving, setSaving] = useState(false);

  // drill-to-source modal
  const [source, setSource] = useState<PsaSourceResult | null>(null);
  const [sourceTitle, setSourceTitle] = useState('');
  const [sourceLoading, setSourceLoading] = useState(false);

  useEffect(() => {
    authAPI.checkAdminAuth().then(res => {
      if (res.authenticated && res.user) setRole(res.user.role ?? 'sasadmin');
    }).catch(() => setRole('sys-operator'));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [ov, cats, temp, day, dem] = await Promise.all([
        postAnalysisAPI.getOverview(), postAnalysisAPI.getCategories(),
        postAnalysisAPI.getTemporal(), postAnalysisAPI.getDaily(), postAnalysisAPI.getDemographics(),
      ]);
      setOverview(ov); setCategories(cats); setTemporal(temp); setDaily(day); setDemo(dem);
      // No.4 panels load independently: a failure here must not blank the whole page
      postAnalysisAPI.getNo4PerMember().then(setNo4).catch(() => setNo4(null));
      postAnalysisAPI.getNo4TypeMix().then(setMix).catch(() => setMix(null));
      postAnalysisAPI.getNo4Parity().then(setParity).catch(() => setParity(null));
      postAnalysisAPI.getReport0807().then(setR0807).catch(() => setR0807(null));
      postAnalysisAPI.getReport0807Parity().then(setR0807Parity).catch(() => setR0807Parity(null));
    } catch (e: any) { setError(e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isSasadmin) loadAll(); }, [isSasadmin, loadAll]);

  useEffect(() => {
    if (!isSasadmin) return;
    postAnalysisAPI.getAdvertisers(genre).then(setAdvertisers).catch(() => setAdvertisers([]));
  }, [genre, isSasadmin]);

  const loadPeriods = useCallback(async () => {
    try {
      const list = await testPeriodsAPI.list();
      setPeriods(list);
      const cur = list.find(p => p.isCurrent);
      if (cur) { setSelFrom(cur.dateFrom); setSelTo(cur.dateTo); }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (isSasadmin) loadPeriods(); }, [isSasadmin, loadPeriods]);

  const rebuild = async () => {
    if (!selFrom || !selTo || selFrom > selTo) { setError(t('postSurvey.badRange', '開始日は終了日以前にしてください')); return; }
    setRebuilding(true); setError(null);
    try { await postAnalysisAPI.rebuild(selFrom, selTo); await loadAll(); }
    catch (e: any) { setError(e?.message || 'Rebuild failed'); }
    finally { setRebuilding(false); }
  };

  const savePeriod = async () => {
    const label = window.prompt(t('postSurvey.periodLabel', '期間名を入力'), `${selFrom} – ${selTo}`);
    if (!label) return;
    setSaving(true); setError(null);
    try { await testPeriodsAPI.create(label, selFrom, selTo, true); await loadPeriods(); }
    catch (e: any) { setError(e?.message || 'save failed'); }
    finally { setSaving(false); }
  };

  const openSource = async (params: Record<string, string>, title: string) => {
    setSourceTitle(title); setSourceLoading(true); setSource({ total: 0, limit: 0, offset: 0, rows: [] });
    try { setSource(await postAnalysisAPI.getSource({ ...params, limit: 200 })); }
    catch { setSource({ total: 0, limit: 0, offset: 0, rows: [] }); }
    finally { setSourceLoading(false); }
  };

  const sm = overview?.summary; const metrics = overview?.metrics;

  const catChart = useMemo(() => categories
    .filter(c => c.category !== 'youtube')
    .slice(0, 14)
    .map(c => ({ name: catLabel(c.category), value: c.events, cat: c.category, isG: c.isGambling })), [categories]);

  const platData = useMemo(() => {
    const p = metrics?.platforms || {};
    return Object.entries(p).map(([k, v]) => ({
      name: k === 'x_twitter' ? 'X' : k[0].toUpperCase() + k.slice(1),
      key: k, ads: v.events, gambling: v.gambling, rate: v.gambling_rate, reach: v.gambling_reach,
    })).sort((a, b) => b.ads - a.ads);
  }, [metrics]);

  const genreData = useMemo(() => {
    const g = metrics?.genres || {};
    return GENRES.filter(k => g[k]).map(k => ({ name: catLabel(k), value: g[k].n, cat: k }));
  }, [metrics]);

  const legalData = useMemo(() => {
    const l = metrics?.legal_split || {};
    return ['legal', 'gray', 'illegal'].filter(k => l[k]).map(k => ({ name: LEGAL_LABEL[k], value: l[k], key: k }));
  }, [metrics]);

  const hourChart = useMemo(() => temporal ? HOURS.map(h => ({
    hour: `${h}`, all: temporal.hourAll[h] || 0, gambling: temporal.hourGambling[h] || 0,
  })) : [], [temporal]);

  const heatMax = useMemo(() => temporal ? Math.max(1, ...temporal.heatmap.flat()) : 1, [temporal]);

  if (role && !isSasadmin) {
    return (
      <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] flex items-center justify-center p-6">
        <div className="text-center text-gray-500 dark:text-gray-400">
          <ShieldAlert className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>{t('postSurvey.adminOnly', 'この分析は管理者(sasadmin)専用です。')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612]">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Microscope className="w-6 h-6 text-fuchsia-500" />
            {t('postSurvey.title', '調査後 高精度分析')}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-2 py-1">
              <CalendarRange className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={periods.find(p => p.dateFrom === selFrom && p.dateTo === selTo)?.id ?? ''}
                onChange={e => { const p = periods.find(x => String(x.id) === e.target.value); if (p) { setSelFrom(p.dateFrom); setSelTo(p.dateTo); } }}
                className="text-xs bg-transparent text-gray-700 dark:text-gray-200 outline-none max-w-[160px]">
                <option value="">{t('postSurvey.customRange', 'カスタム範囲')}</option>
                {periods.map(p => <option key={p.id} value={p.id}>{p.label}{p.isCurrent ? ' ★' : ''}</option>)}
              </select>
              <input type="date" value={selFrom} max={selTo} onChange={e => setSelFrom(e.target.value)}
                className="text-xs px-1 py-0.5 rounded border border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
              <span className="text-gray-400">–</span>
              <input type="date" value={selTo} min={selFrom} onChange={e => setSelTo(e.target.value)}
                className="text-xs px-1 py-0.5 rounded border border-gray-200 dark:border-gray-700 dark:bg-gray-900 dark:text-white" />
              <button onClick={savePeriod} disabled={saving} title={t('postSurvey.saveAsPeriod', 'この範囲を期間として保存し現在の期間にする')}
                className="text-gray-400 hover:text-fuchsia-500 disabled:opacity-40 px-1">
                <Save className="w-4 h-4" />
              </button>
            </div>
            <button onClick={rebuild} disabled={rebuilding || loading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-fuchsia-600 hover:bg-fuchsia-700 text-white disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${rebuilding ? 'animate-spin' : ''}`} />
              {rebuilding ? t('postSurvey.rebuilding', '再構築中…(約15秒)') : t('postSurvey.rebuildRange', 'この期間で再構築')}
            </button>
          </div>
        </div>

        {/* provenance banner */}
        <div className="mb-5 rounded-xl border border-fuchsia-200 dark:border-fuchsia-900/40 bg-fuchsia-50 dark:bg-fuchsia-900/10 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 text-fuchsia-500 flex-shrink-0" />
          <div>
            {t('postSurvey.provenance', 'この画面は元の捕捉データから再計算した高精度の補正済み分析です(全広告主の監査によりギャンブル誤検知を除去)。数値は各行の捕捉広告まで追跡できます。')}
            {sm && (
              <span className="ml-1 font-medium">
                {t('postSurvey.baselineCorrected', '元分類')} {fmt(sm.baselineGambling)} → <span className="text-fuchsia-600 dark:text-fuchsia-400">{fmt(sm.gambling)}</span>
              </span>
            )}
            {overview?.run && (
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                · {overview.run.windowFrom?.slice(0, 10)} – {overview.run.windowTo?.slice(0, 10)}
                {overview.run.generatedAt && ` · ${new Date(overview.run.generatedAt).toLocaleString('ja-JP')}`}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-center justify-between bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-2 text-sm text-red-700 dark:text-red-300">
            <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        )}

        {loading && !overview && <div className="py-20 text-center text-gray-400">{t('common.loading', '読み込み中…')}</div>}

        {!overview?.hasData && !loading && (
          <div className="py-16 text-center text-sm text-gray-400">
            {t('postSurvey.noData', 'まだ分析がありません。「分析を再構築」を押してください。')}
          </div>
        )}

        {overview?.hasData && sm && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <KpiCard label={t('postSurvey.participants', '参加者')} value={fmt(sm.participants)} icon={Users2} color="border-blue-500" />
              <KpiCard label={t('postSurvey.impressions', '広告表示回数')} value={fmt(sm.impressions)} sub={`${fmt(sm.totalEvents)} events`} icon={Layers} color="border-sky-500" />
              <KpiCard label={t('postSurvey.gamblingReach', 'ギャンブル接触人数')} value={`${fmt(sm.gamblingReach)}/${fmt(sm.participants)}`} sub={sm.participants ? `${Math.round(100 * sm.gamblingReach / sm.participants)}%` : ''} icon={ShieldAlert} color="border-amber-500" />
              <KpiCard label={t('postSurvey.gambling', 'ギャンブル広告')} value={fmt(sm.gambling)} sub={`${fmt(sm.gamblingCreatives)} 種`} icon={ShieldAlert} color="border-orange-500" />
              <KpiCard label={t('postSurvey.illegal', '違法カジノ')} value={fmt(sm.illegal)} sub="METAWIN" icon={ShieldAlert} color="border-red-500"
                onClick={() => openSource({ category: 'gambling_online' }, `${catLabel('gambling_online')} — ${t('postSurvey.sourceAds', '捕捉広告')}`)} />
              <KpiCard label={t('postSurvey.effReach', '有効リーチ(3回+)')} value={fmt(sm.effectiveReach3)} icon={Users2} color="border-violet-500" />
            </div>

            {/* category distribution */}
            <Panel title={t('postSurvey.catDist', '広告カテゴリ分布(内容特定分)')} icon={Layers}>
              <ResponsiveContainer width="100%" height={Math.max(280, catChart.length * 26)}>
                <BarChart data={catChart} layout="vertical" margin={{ left: 20, right: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: any) => fmt(Number(v))} />
                  <Bar dataKey="value" isAnimationActive={false} radius={[0, 3, 3, 0]}
                    onClick={(d: any) => d?.cat && openSource({ category: d.cat }, `${catLabel(d.cat)} — ${t('postSurvey.sourceAds', '捕捉広告')}`)}
                    cursor="pointer">
                    {catChart.map((d, i) => <Cell key={i} fill={d.isG ? (d.cat === 'gambling_online' ? '#ef4444' : d.cat === 'gambling_gacha_premium' ? '#f59e0b' : '#fb7185') : '#3b82f6'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-gray-400 mt-1">{t('postSurvey.catHint', '棒をクリックすると元の捕捉広告に追跡できます。ギャンブルは暖色で強調。')}</p>
            </Panel>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* platform */}
              <Panel title={t('postSurvey.byPlatform', 'プラットフォーム別 広告量・ギャンブル')} icon={Layers}>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={platData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: any) => fmt(Number(v))} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="l" dataKey="ads" name={t('postSurvey.allAds', '全広告')} fill="#93c5fd" isAnimationActive={false} radius={[3, 3, 0, 0]} />
                    <Bar yAxisId="r" dataKey="gambling" name={t('postSurvey.gambling', 'ギャンブル')} fill="#ef4444" isAnimationActive={false} radius={[3, 3, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </Panel>

              {/* legal + genre donuts */}
              <Panel title={t('postSurvey.legalGenre', '法的区分 / 種別')} icon={ShieldAlert}>
                <div className="grid grid-cols-2 gap-2">
                  <Donut data={legalData} colors={legalData.map(d => LEGAL_COLOR[d.key])} label={t('postSurvey.legal', '法的区分')} />
                  <Donut data={genreData} colors={genreData.map(d => catColor(d.cat))} label={t('postSurvey.genre', '種別')}
                    onSlice={(d: any) => d?.cat && openSource({ category: d.cat }, `${catLabel(d.cat)} — ${t('postSurvey.sourceAds', '捕捉広告')}`)} />
                </div>
              </Panel>
            </div>

            {/* temporal */}
            <Panel title={t('postSurvey.temporal', '時間帯パターン')} icon={Clock}>
              <ResponsiveContainer width="100%" height={230}>
                <ComposedChart data={hourChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v: any) => fmt(Number(v))} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="l" dataKey="all" name={t('postSurvey.allAds', '全広告')} fill="#bfdbfe" isAnimationActive={false} />
                  <Line yAxisId="r" dataKey="gambling" name={t('postSurvey.gambling', 'ギャンブル')} stroke="#ef4444" strokeWidth={2} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
              {temporal && (
                <div className="mt-4 overflow-x-auto">
                  <div className="text-xs text-gray-400 mb-1">{t('postSurvey.heatmap', 'ギャンブル接触 曜日×時刻')}</div>
                  <table className="border-collapse">
                    <tbody>
                      {temporal.heatmap.map((row, di) => (
                        <tr key={di}>
                          <td className="pr-1 text-[10px] text-gray-400">{DOW_JA[di]}</td>
                          {row.map((v, hi) => (
                            <td key={hi} title={`${DOW_JA[di]} ${hi}:00 — ${v}`}
                              style={{ background: v ? `rgba(239,68,68,${0.15 + 0.85 * v / heatMax})` : 'transparent' }}
                              className="w-3.5 h-3.5 border border-gray-100 dark:border-gray-800" />
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            {/* demographics + cohorts */}
            {demo && (
              <Panel title={t('postSurvey.audience', '利用者層')} icon={Users2}>
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-3">
                  {t('postSurvey.demoCaveat', `属性(年齢・性別)は任意回答で ${demo.nWithDemo}/${demo.nActive} 名のみ → 参考値。主分析は行動コホート。`)}
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('postSurvey.cohorts', '行動コホート(k-means)')}</div>
                    <table className="w-full text-sm">
                      <thead><tr className="text-[11px] uppercase text-gray-400">
                        <th className="text-left py-1">{t('postSurvey.cohort', 'コホート')}</th><th className="text-right">人数</th><th className="text-right">平均広告</th><th className="text-right">ギャンブル%</th><th className="text-right">到達</th>
                      </tr></thead>
                      <tbody>
                        {demo.cohorts.map((c, i) => (
                          <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                            <td className="py-1.5">{c.name}</td><td className="text-right">{c.members}</td>
                            <td className="text-right">{fmt(c.avgAds)}</td><td className="text-right text-red-500 font-medium">{c.gamblingRate}%</td>
                            <td className="text-right">{c.gamblingReach}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">{t('postSurvey.ageGender', '年齢・性別(subsample)')}</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={demo.byAge.map(a => ({ name: a.age, ads: a.ads, gambling: a.gambling }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v: any) => fmt(Number(v))} />
                        <Bar dataKey="ads" fill="#3b82f6" isAnimationActive={false} radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="text-xs text-gray-500 mt-1">
                      {demo.byGender.map(g => `${g.gender}: ${g.members}名 / ギャンブル${g.gambling}`).join(' · ')}
                    </div>
                  </div>
                </div>
              </Panel>
            )}

            {/* top advertisers */}
            <Panel title={t('postSurvey.topAdvertisers', '主なギャンブル広告主')} icon={Building2}>
              <div className="flex gap-1.5 flex-wrap mb-3">
                {['all', ...GENRES.filter(g => (metrics?.genres || {})[g])].map(g => (
                  <button key={g} onClick={() => setGenre(g)}
                    className={`px-2.5 py-1 rounded-full text-xs border ${genre === g ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300'}`}>
                    {g === 'all' ? t('postSurvey.allGenres', 'すべて') : catLabel(g)}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-[11px] uppercase text-gray-400">
                    <th className="text-left py-1">{t('postSurvey.advertiser', '広告主')}</th><th className="text-left">種別</th><th className="text-right">表示</th><th className="text-right">到達</th><th></th>
                  </tr></thead>
                  <tbody>
                    {advertisers.slice(0, 20).map((a, i) => (
                      <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-1.5 truncate max-w-[260px]">{a.advertiser}</td>
                        <td><span className="inline-block px-1.5 py-0.5 rounded text-[10px]" style={{ background: catColor(a.category) + '22', color: catColor(a.category) }}>{catLabel(a.category)}</span></td>
                        <td className="text-right">{fmt(a.impressions)}</td>
                        <td className="text-right">{a.reach}</td>
                        <td className="text-right">
                          <button className="text-fuchsia-500 hover:text-fuchsia-600" title={t('postSurvey.trace', '捕捉元へ')}
                            onClick={() => openSource({ advertiser: a.advertiser }, `${a.advertiser} — ${t('postSurvey.sourceAds', '捕捉広告')}`)}>
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            {/* ---- No.4: per-user-ID counts + ad-type composition (report §8.5/§8.6) ---- */}
            {no4 && (
              <Panel title={`${t('postSurvey.no4PerMember', 'ユーザーID別 ギャンブル広告件数')} — ${t('postSurvey.no4Section', 'No.4')}`} icon={Users2}>
                {parity && (
                  <div className={`mb-3 rounded-lg px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 ${
                    parity.allOk ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                                 : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>
                    <span className="font-semibold">
                      {parity.allOk ? `✓ ${t('postSurvey.no4ParityOk', '配布レポートと一致')}` : `⚠ ${t('postSurvey.no4ParityNg', '配布レポートと不一致')}`}
                    </span>
                    {parity.report && <span className="opacity-80">{parity.report.edition} ({parity.report.rev})</span>}
                    {parity.python && <span className="opacity-70">{t('postSurvey.no4Python', '検証スクリプト')}: {new Date(parity.python.computedAt).toLocaleString('ja-JP')}</span>}
                    {!parity.allOk && (
                      <span className="w-full mt-1">
                        {parity.checks.filter(c => !c.ok).map(c => (
                          <span key={c.key} className="mr-3">{c.key}: {t('postSurvey.no4Expected', 'レポート値')} {fmt(c.expected ?? undefined)} / {t('postSurvey.no4Sql', '本画面(SQL)')} {fmt(c.sql ?? undefined)}</span>
                        ))}
                      </span>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
                  {([
                    ['no4Members', '接触者', no4.summary.members],
                    ['no4Ads', 'ギャンブル広告', no4.summary.total],
                    ['no4Median', '中央値(接触者)', no4.summary.median],
                    ['no4Max', '最多', no4.summary.max],
                    ['no4Top10', '上位10名の占有率', `${no4.summary.top10Share}%`],
                    ['no4Ge3', '3件以上', no4.summary.ge3],
                  ] as const).map(([k, def, v]) => (
                    <div key={k} className="bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                      <div className="text-lg font-bold text-gray-800 dark:text-gray-100">{typeof v === 'number' ? fmt(v) : v}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">{t(`postSurvey.${k}`, def)}</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {t('postSurvey.no4PerMemberDesc', '接触は極度に集中しています。').replace('{{pct}}', String(no4.summary.top10Share))}
                </p>
                <div className="overflow-auto max-h-[420px] border border-gray-100 dark:border-gray-700 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                      <tr>
                        <th className="text-left px-2 py-1.5">{t('postSurvey.no4Uid', 'ユーザーID')}</th>
                        <th className="text-right px-2 py-1.5">{t('postSurvey.no4Total', '計')}</th>
                        <th className="text-right px-2 py-1.5">{catLabel('gambling_public')}</th>
                        <th className="text-right px-2 py-1.5">{catLabel('gambling_pachinko')}</th>
                        <th className="text-right px-2 py-1.5">{catLabel('gambling_gacha_premium')}</th>
                        <th className="text-right px-2 py-1.5">{catLabel('gambling_online')}</th>
                        <th className="text-left px-2 py-1.5">{t('postSurvey.no4MainPlat', '主な媒体')}</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {no4.rows.map(r => (
                        <tr key={r.code} className="border-t border-gray-100 dark:border-gray-700">
                          <td className="px-2 py-1 font-mono">{r.code}</td>
                          <td className="px-2 py-1 text-right font-semibold">{fmt(r.total)}</td>
                          <td className="px-2 py-1 text-right">{r.public}</td>
                          <td className="px-2 py-1 text-right">{r.pachinko}</td>
                          <td className="px-2 py-1 text-right">{r.oripa}</td>
                          <td className={`px-2 py-1 text-right ${r.casino ? 'text-red-500 font-semibold' : ''}`}>{r.casino}</td>
                          <td className="px-2 py-1 text-gray-500">{platLabel(r.mainPlatform)}</td>
                          <td className="px-2 py-1 text-right">
                            <button className="text-fuchsia-500 hover:text-fuchsia-600" title={t('postSurvey.trace', '捕捉元へ')}
                              onClick={() => openSource({ code: r.code, gamblingOnly: 'true' }, `${r.code} — ${t('postSurvey.sourceAds', '捕捉広告')}`)}>
                              <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">{t('postSurvey.no4Ge3Note', '')}</p>
              </Panel>
            )}

            {mix && (
              <Panel title={t('postSurvey.no4TypeMix', 'ギャンブル広告の種類構成')} icon={Layers}>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('postSurvey.no4TypeMixDesc', '')}</p>
                <div className="space-y-1.5 mb-4">
                  {mix.categories.map(c => {
                    const totalAds = mix.categories.reduce((s, x) => s + x.ads, 0) || 1;
                    const pct = (100 * c.ads) / totalAds;
                    return (
                      <div key={c.category} className="flex items-center gap-2 text-xs">
                        <span className="w-40 shrink-0 truncate" title={catLabel(c.category)}>{catLabel(c.category)}</span>
                        <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden cursor-pointer"
                          onClick={() => openSource({ category: c.category }, `${catLabel(c.category)} — ${t('postSurvey.sourceAds', '捕捉広告')}`)}>
                          <div className="h-full rounded" style={{ width: `${Math.max(pct, 0.4)}%`, background: catColor(c.category) }} />
                        </div>
                        <span className="w-28 shrink-0 text-right tabular-nums">
                          {fmt(c.ads)} <span className="text-gray-400">({pct.toFixed(1)}%)</span>
                        </span>
                        <span className="w-16 shrink-0 text-right text-gray-400">{c.members}名</span>
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  {([
                    ['no4MemberLevel', '会員単位の公営競技比率', `n=${mix.memberLevel.n}`],
                    ['no4MedianShare', '中央値', mix.memberLevel.medianPublicShare === null ? '—' : `${mix.memberLevel.medianPublicShare}%`],
                    ['no4MeanShare', '平均', mix.memberLevel.meanPublicShare === null ? '—' : `${mix.memberLevel.meanPublicShare}%`],
                    ['no4Iqr', '四分位(25–75%)', `${mix.memberLevel.p25 ?? '—'}–${mix.memberLevel.p75 ?? '—'}%`],
                  ] as const).map(([k, def, v]) => (
                    <div key={k} className="bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                      <div className="text-base font-bold text-gray-800 dark:text-gray-100">{v}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">{t(`postSurvey.${k}`, def)}</div>
                    </div>
                  ))}
                </div>
                {!mix.severity.available && (
                  <div className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 rounded-lg px-3 py-2">
                    {t('postSurvey.no4SeverityNA', '')}
                  </div>
                )}
              </Panel>
            )}

            {/* ---- 2026-08-07 additional analysis report (No.1-No.4) ---- */}
            {r0807 && (
              <Panel title={`${t('postSurvey.r0807Title', '追加分析レポート (2026-08-07)')} — No.2 / No.3 / No.4`} icon={Layers}>
                {r0807Parity && (
                  <div className={`mb-3 rounded-lg px-3 py-2 text-xs flex flex-wrap items-center gap-x-3 gap-y-1 ${
                    r0807Parity.allOk ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                                      : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'}`}>
                    <span className="font-semibold">
                      {r0807Parity.allOk ? `✓ ${t('postSurvey.r0807Ok', '配布レポートと一致')} (${(r0807Parity.checks ?? []).length})`
                                         : `⚠ ${t('postSurvey.r0807Ng', '配布レポートと不一致')}`}
                    </span>
                    {r0807Parity.report && <span className="opacity-80">{r0807Parity.report.edition}</span>}
                    {!r0807Parity.allOk && (
                      <span className="w-full mt-1">
                        {(r0807Parity.checks ?? []).filter(c => !c.ok).map(c => (
                          <span key={c.key} className="mr-3">{c.key}: {t('postSurvey.no4Expected', 'レポート値')} {fmt(c.expected ?? undefined)} / {t('postSurvey.no4Sql', '本画面(SQL)')} {fmt(c.sql ?? undefined)}</span>
                        ))}
                      </span>
                    )}
                  </div>
                )}

                {/* No.2 — activity comparison. 'All' is live SQL; A/B/C are frozen group
                    aggregates, because no PGSI data exists on this server. */}
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                  {t('postSurvey.r0807No2', 'No.2 重症度グループ別の調査状況')}
                </h4>
                <div className="overflow-auto border border-gray-100 dark:border-gray-700 rounded-lg mb-4">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                      <tr>
                        <th className="text-left px-2 py-1.5">{t('postSurvey.r0807Metric', '項目')}</th>
                        <th className="text-right px-2 py-1.5">{t('postSurvey.r0807All', '全体 (SQL)')}</th>
                        <th className="text-right px-2 py-1.5">A</th>
                        <th className="text-right px-2 py-1.5">B</th>
                        <th className="text-right px-2 py-1.5">C</th>
                      </tr>
                    </thead>
                    <tbody>
                      {([
                        ['対象者数', (g: any) => g.n, r0807.activityAll?.n],
                        ['1日平均の広告取得者数', (g: any) => g.daily_collected, r0807.activityAll?.dailyCollected],
                        ['VPN接続時間(分/人・接続日)', (g: any) => g.vpn_min_per_day, r0807.activityAll?.vpnMinPerDay],
                        ['VPN接続日数(日/人)', (g: any) => g.vpn_days_per_member, r0807.activityAll?.vpnDaysPerMember],
                        ['全広告(件/人・日)', (g: any) => g.ads_per_member_day, r0807.activityAll?.adsPerMemberDay],
                        ['ギャンブル広告(件/人・日)', (g: any) => g.gam_per_member_day, r0807.activityAll?.gamPerMemberDay],
                        ['ギャンブル広告の割合(%)', (g: any) => g.gam_pct, r0807.activityAll?.gamPct],
                      ] as const).map(([label, pick, live], i) => (
                        <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                          <td className="px-2 py-1">{label}</td>
                          <td className="px-2 py-1 text-right font-semibold tabular-nums">{live}</td>
                          {(['A', 'B', 'C'] as const).map(g => (
                            <td key={g} className="px-2 py-1 text-right tabular-nums text-gray-600 dark:text-gray-300">
                              {r0807.report?.no2?.[g] ? (pick as any)(r0807.report.no2[g]) : '—'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 mb-4">
                  {t('postSurvey.r0807GroupNote', 'A/B/C列は配布レポートの凍結値です。重症度(PGSI)データは本サーバに保持していないため、本画面ではライブ再計算できません。全体列のみSQLで都度再計算し、上のバッジで配布レポートとの一致を検証しています。')}
                </p>

                {/* No.3 — pachinko sub-classification, live from the same map as the report */}
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                  {t('postSurvey.r0807No3', 'No.3 パチンコ・パチスロの内訳 / 金融広告')}
                </h4>
                <div className="space-y-1.5 mb-3">
                  {(r0807.no3?.pachinko ?? []).map(p2 => {
                    const tot = (r0807.no3?.pachinko ?? []).reduce((s2, x) => s2 + x.ads, 0) || 1;
                    return (
                      <div key={p2.sub} className="flex items-center gap-2 text-xs">
                        <span className="w-40 shrink-0 truncate" title={p2.sub}>{p2.sub}</span>
                        <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                          <div className="h-full rounded" style={{ width: `${Math.max((100 * p2.ads) / tot, 0.5)}%`,
                            background: p2.sub === '要確認' ? '#ef4444' : '#6366f1' }} />
                        </div>
                        <span className="w-24 shrink-0 text-right tabular-nums">{fmt(p2.ads)}</span>
                        <span className="w-14 shrink-0 text-right text-gray-400">{p2.reach}名</span>
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  {([
                    ['r0807Fin', 'FX・証券(金融取引)', `${fmt((r0807.no3?.financial?.ads ?? 0))}件 / ${(r0807.no3?.financial?.advertisers ?? 0)}社`],
                    ['r0807Casino', 'オンラインカジノ', `${fmt((r0807.no3?.casino ?? []).reduce((s2, x) => s2 + x.ads, 0))}件`],
                    ['r0807Public', '公営競技', `${fmt((r0807.no3?.public ?? []).reduce((s2, x) => s2 + x.ads, 0))}件`],
                    ['r0807Oripa', 'オンラインくじ・オリパ', `${fmt((r0807.no3?.oripa ?? []).reduce((s2, x) => s2 + x.ads, 0))}件`],
                  ] as const).map(([k, def, v]) => (
                    <div key={k} className="bg-gray-50 dark:bg-gray-700/40 rounded-lg px-3 py-2">
                      <div className="text-base font-bold text-gray-800 dark:text-gray-100">{v}</div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400">{t(`postSurvey.${k}`, def)}</div>
                    </div>
                  ))}
                </div>

                {/* No.4 — per-user share (needs the TOTAL ads denominator, new in this report) */}
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                  {t('postSurvey.r0807No4', 'No.4 ユーザーごとのギャンブル広告表示割合')}
                </h4>
                <div className="overflow-auto max-h-[420px] border border-gray-100 dark:border-gray-700 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                      <tr>
                        <th className="text-left px-2 py-1.5">{t('postSurvey.no4Uid', 'ユーザーID')}</th>
                        <th className="text-right px-2 py-1.5">{t('postSurvey.r0807TotalAds', '全広告')}</th>
                        <th className="text-right px-2 py-1.5">{t('postSurvey.gambling', 'ギャンブル')}</th>
                        <th className="text-right px-2 py-1.5">{t('postSurvey.r0807Share', '割合')}</th>
                        <th className="text-right px-2 py-1.5">{catLabel('gambling_public')}</th>
                        <th className="text-right px-2 py-1.5">{catLabel('gambling_pachinko')}</th>
                        <th className="text-right px-2 py-1.5">{catLabel('gambling_gacha_premium')}</th>
                        <th className="text-right px-2 py-1.5">{catLabel('gambling_online')}</th>
                        <th className="text-left px-2 py-1.5">{t('postSurvey.no4MainPlat', '主な媒体')}</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(r0807.users ?? []).map(u => (
                        <tr key={u.code} className="border-t border-gray-100 dark:border-gray-700">
                          <td className="px-2 py-1 font-mono">{u.code}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmt(u.total)}</td>
                          <td className="px-2 py-1 text-right font-semibold tabular-nums">{u.gambling}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{u.sharePct.toFixed(2)}%</td>
                          <td className="px-2 py-1 text-right">{u.public || '—'}</td>
                          <td className="px-2 py-1 text-right">{u.pachinko || '—'}</td>
                          <td className="px-2 py-1 text-right">{u.oripa || '—'}</td>
                          <td className={`px-2 py-1 text-right ${u.casino ? 'text-red-500 font-semibold' : ''}`}>{u.casino || '—'}</td>
                          <td className="px-2 py-1 text-gray-500">{u.main_platform ? platLabel(u.main_platform) : '—'}</td>
                          <td className="px-2 py-1 text-right">
                            <button className="text-fuchsia-500 hover:text-fuchsia-600" title={t('postSurvey.trace', '捕捉元へ')}
                              onClick={() => openSource({ code: u.code }, `${u.code} — ${t('postSurvey.sourceAds', '捕捉広告')}`)}>
                              <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">
                  {t('postSurvey.r0807Denom', '割合＝ギャンブル広告数÷全広告表示数×100。分母は取得した広告イベント全件(計測ビーコンを含む)で、配布レポートと同一基準です。')}
                </p>
              </Panel>
            )}

            {/* daily */}
            <Panel title={t('postSurvey.daily', '日次推移(全広告 vs ギャンブル)')} icon={Clock}>
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={daily.map(d => ({ ...d, day: d.day.slice(5) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10 }} /><YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v: any) => fmt(Number(v))} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="l" dataKey="ads" name={t('postSurvey.allAds', '全広告')} fill="#93c5fd" isAnimationActive={false} />
                  <Line yAxisId="r" dataKey="gambling" name={t('postSurvey.gambling', 'ギャンブル')} stroke="#ef4444" strokeWidth={2} dot={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Panel>
          </>
        )}
      </div>

      {/* drill-to-source modal */}
      {source !== null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setSource(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-5xl w-full my-8" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                <Database className="w-4 h-4 text-fuchsia-500" /> {sourceTitle}
                {source.total > 0 && <span className="text-xs text-gray-400">({fmt(source.total)} {t('postSurvey.rows', '件')})</span>}
              </div>
              <button onClick={() => setSource(null)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {sourceLoading ? <div className="py-10 text-center text-gray-400">{t('common.loading', '読み込み中…')}</div> : (
                <table className="w-full text-xs">
                  <thead><tr className="text-[10px] uppercase text-gray-400 border-b border-gray-100 dark:border-gray-800">
                    <th className="text-left py-1.5">{t('postSurvey.shownAt', '時刻(JST)')}</th>
                    <th className="text-left">{t('postSurvey.member', '会員コード')}</th>
                    <th className="text-left">{t('postSurvey.platform', '媒体')}</th>
                    <th className="text-left">{t('postSurvey.advertiser', '広告主')}</th>
                    <th className="text-left">{t('postSurvey.correction', '元→補正')}</th>
                    <th className="text-left">{t('postSurvey.adUrl', '広告リンク先 / 捕捉URL')}</th>
                  </tr></thead>
                  <tbody>
                    {source.rows.map(r => (
                      <tr key={r.adEventId} className="border-b border-gray-50 dark:border-gray-800/50">
                        <td className="py-1.5 whitespace-nowrap text-gray-500">{new Date(r.eventTs).toLocaleString('ja-JP')}</td>
                        <td className="whitespace-nowrap font-mono text-[11px] text-gray-700 dark:text-gray-300">{r.code || '—'}</td>
                        <td>{r.platform === 'x_twitter' ? 'X' : r.platform}</td>
                        <td className="max-w-[160px] truncate" title={r.advertiser}>{r.advertiser}</td>
                        <td className="whitespace-nowrap">
                          <span className="text-gray-400">{catLabel(r.baselineCategory || '—')}</span>
                          <span className="mx-1">→</span>
                          <span style={{ color: catColor(r.category) }}>{catLabel(r.category || '—')}</span>
                          {r.correctionSource !== 'baseline' && <span className="ml-1 text-[9px] text-fuchsia-400">({r.correctionSource})</span>}
                        </td>
                        <td className="max-w-[260px] truncate text-blue-500" title={r.advertiserUrl || r.url}>
                          {(r.advertiserUrl || r.url)
                            ? <a href={r.advertiserUrl || r.url} target="_blank" rel="noopener noreferrer">{r.advertiserUrl || r.url}</a>
                            : (r.host || '—')}
                        </td>
                      </tr>
                    ))}
                    {source.rows.length === 0 && !sourceLoading && (
                      <tr><td colSpan={6} className="py-8 text-center text-gray-400">{t('postSurvey.noSource', '該当データなし')}</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Panel({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 md:p-6 mb-6">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400" /> {title}
      </h3>
      {children}
    </div>
  );
}

function Donut({ data, colors, label, onSlice }: { data: { name: string; value: number }[]; colors: string[]; label: string; onSlice?: (d: any) => void }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="text-center">
      <div className="text-[11px] text-gray-400 mb-1">{label}</div>
      <ResponsiveContainer width="100%" height={170}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={38} outerRadius={62}
            isAnimationActive={false} onClick={onSlice} cursor={onSlice ? 'pointer' : undefined}>
            {data.map((_, i) => <Cell key={i} fill={colors[i]} />)}
          </Pie>
          <Tooltip formatter={(v: any) => `${fmt(Number(v))} (${total ? Math.round(100 * Number(v) / total) : 0}%)`} />
        </PieChart>
      </ResponsiveContainer>
      <div className="text-[10px] text-gray-500 space-y-0.5">
        {data.map((d, i) => (
          <div key={i}><span className="inline-block w-2 h-2 rounded-sm mr-1" style={{ background: colors[i] }} />{d.name} <b>{fmt(d.value)}</b></div>
        ))}
      </div>
    </div>
  );
}
